import React from "react";
import { Box, Flex, VStack, Text, Grid, FormControl, FormLabel, Image as ChakraImage, InputGroup, InputRightElement } from "@chakra-ui/react";
import Image from "next/image";
import { P } from "../components/Type";
import Button from "../components/Button";
import { Trans, t, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useRouter } from "next/router";
import { Formik, Form, Field } from "formik";
import { object, string } from "yup";
import SecureIcon from "../assets/secure.png";
import SearchIcon from "../assets/search.png";
import { Input } from "../components/Input";
import { useGameVoucherGroups } from "../hooks/game-vouchers/useGameVoucherGroups";
import { useTokoProducts } from "../hooks/useTokoProducts";
import { TokoProductCard } from "./Toko/TokoProductCard";

export const TokoForm = ({
  streamerId,
  streamerUsername,
  currency,
}) => {
  const [activeTab, setActiveTab] = React.useState("PRODUCTS");
  const [keyword, setKeyword] = React.useState("");
  const { _ } = useLingui();
  const router = useRouter();
  const { username } = router.query;
  const storeUsername = streamerUsername || username;

  // debounce search -> query keyword, and accumulate pages for "Selanjutnya"
  const [debounced, setDebounced] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [products, setProducts] = React.useState([]);
  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(keyword.trim()), 400);
    return () => clearTimeout(id);
  }, [keyword]);
  React.useEffect(() => {
    setPage(1);
    setProducts([]);
  }, [debounced]);

  const { data: productPage, isFetching } = useTokoProducts({
    username: storeUsername,
    keyword: debounced,
    page,
  });
  React.useEffect(() => {
    if (!productPage) return;
    setProducts((prev) => (page === 1 ? productPage.products || [] : [...prev, ...(productPage.products || [])]));
  }, [productPage, page]);
  const hasNextPage = (productPage?.page?.current || 1) < (productPage?.page?.total || 0);

  const handleBuy = (product) => {
    // ponytail: route to product/checkout page once it exists
    router.push(`/${storeUsername}/toko/${product.slug}`);
  };

  return (
    <Box>
        <Text
            fontSize={{ base: "16px", md: "20px" }}
            fontFamily="Comfortaa"
            lineHeight={{ base: "20px", md: "24px" }}
            color="#1a202c"
            fontWeight="400"
            mb={{ base: "12px", md: "10px" }}
            mt={{ base: "12px", md: "10px" }}
        >
            {activeTab === "PRODUCTS" ? <Trans>Daftar Produk</Trans> : <Trans>Track Order</Trans>}
        </Text>
      <VStack
        spacing="0"
        align="flex-start"
        w="100%"
        mt={4}
      >
        <Flex direction="row" justifyContent="left" w="100%" overflowX="auto" >
          <Button
            label={_(msg`Produk`)}
            pressed={activeTab === "PRODUCTS"}
            backgroundColor={
              activeTab === "PRODUCTS"
                ? "saweria-yellow.normal"
                : "saweria-white.normal"
            }
            px={{ base: 2, md: 6 }}
            flexShrink={0}
            mr={{ base: 2, md: 4 }}
            mb={{ base: 2, md: 4 }}
            boxShadow="none"
            fontSize={{ base: "sm", md: "lg" }}
            onClick={() => setActiveTab("PRODUCTS")}
          />
          <Button
            label={_(msg`Track Order`)}
            pressed={activeTab === "TRACK"}
            backgroundColor={
              activeTab === "TRACK"
                ? "saweria-yellow.normal"
                : "saweria-white.normal"
            }
            px={{ base: 2, md: 6 }}
            flexShrink={0}
            mb={{ base: 2, md: 4 }}
            boxShadow="none"
            fontSize={{ base: "sm", md: "lg" }}
            onClick={() => setActiveTab("TRACK")}
          />
        </Flex>

        {activeTab === "PRODUCTS" ? (
          <>
          <Box w="100%" mb="16px">            
            <InputGroup mb={{ base: "16px", md: "20px" }}>
              <Input
                placeholder={_(msg`Cari Nama Produk`)}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
               <InputRightElement pointerEvents="none" h="100%">
                <Image src={SearchIcon} alt="" width={16} height={16} />
              </InputRightElement>
            </InputGroup>

            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
              gap="16px"
              w="100%"
            >
              {products.map((p) => (
                <TokoProductCard
                  key={p.id}
                  product={p}
                  currency={currency}
                  onBuy={() => handleBuy(p)}
                />
              ))}
            </Grid>

            {hasNextPage && (
              <Flex justify="flex-start" mt="16px">
                <Button
                  label={_(msg`Selanjutnya`)}
                  backgroundColor="saweria-green.normal"
                  loading={isFetching}
                  onClick={() => setPage((prev) => prev + 1)}
                />
              </Flex>
            )}
          </Box>
          </>
        ) : (
          <Box w="100%">
            <Text
              w="100%"
              lineHeight="20px"
              fontFamily="IBM Plex Mono"
              color="#1a202c"
              textAlign="left"
              display="inline-block"
              mb="17px"
            >
              <Trans>Lacak status pengiriman produk di sini</Trans>
            </Text>
            <Formik
              initialValues={{ trackId: "" }}
              validationSchema={object({
                trackId: string().required(_(msg`Track ID tidak boleh kosong`))
              })}
              onSubmit={(values, { setSubmitting }) => {
                if (values.trackId) {
                  window.open(`/toko/track/${values.trackId}`, '_blank');
                  setSubmitting(false);
                }
              }}
            >
              {({ errors, touched, isSubmitting }) => (
                <Form>
                  <VStack spacing={4} align="flex-start">
                    <FormControl isInvalid={errors.trackId && touched.trackId}>
                      <FormLabel htmlFor="trackId" fontWeight="bold">
                        <Trans>Track ID</Trans>
                      </FormLabel>
                      <Field
                        as={Input}
                        id="trackId"
                        name="trackId"
                        placeholder={_(msg`Masukkan Track ID`)}
                      />
                      {errors.trackId && touched.trackId && (
                        <Text color="red.500" fontSize="sm" mt="4px">
                          {errors.trackId}
                        </Text>
                      )}
                    </FormControl>
                    <Button
                      type="submit"
                      label={_(msg`Lacak Pesanan`)}
                      backgroundColor="saweria-green.normal"
                      pressed={isSubmitting}
                    />
                  </VStack>
                </Form>
              )}
            </Formik>
          </Box>
        )}
      </VStack>
    </Box>
  );
};
